<template>
    <md-layout class="ql-pages-container" :style="{overflow: inMargin ? 'visible' : 'hidden', justifyContent: 'center'}">
        <md-layout md-row md-gutter="12" class="ql-page-layout" :class="{'portrait': !landscape, 'md-column': !landscape}">
            <div class="ql-pages-inner" :style="innerMarginStyle">
                <svg class="ql-page-graphics" xmlns="http://www.w3.org/2000/svg" version="1.1"
                    :style="{height: `${svgTrueHeight}px`, overflow: inMargin ? 'visible' : 'hidden'}" v-if="pageReady">
                    <!-- SVG lines connecting cues with cue markers. Y + 1 so it lines up nicely -->
                    <line
                        v-for="cue in positionedCues"
                        :key="cue.id"
                        class="ql-page-cue-line"
                        :x1="cue.lineX1"
                        :y1="cue.lineY1" 
                        :x2="cue.lineX2"
                        :y2="cue.lineY2"
                        stroke-width="1"
                        :stroke-opacity="lineOpacity(cue)"
                        v-bind:style="cueLineOpacityStyle(cue)"
                    />
                </svg>
                <project-page
                    class="md-flex pdf-column pdf-canvas-column"
                    :page="page"
                    :cues="positionedCues"
                    :currentCueLayer="currentCueLayer"
                    :locked="true"
                    :showMode="showMode"
                    :totalPages="1"
                    :landscape="landscape"
                    @pageLoaded="pageLoaded"
                    :style="{order: pdfPaneOrderByCuePlacement()}"
                />
                <!-- {{ selectedScale }} -->
                <project-cue-list v-if="pageReady"
                    class="pdf-column pdf-cues-column"
                    :cues="positionedCues"
                    :locked="true"
                    :openCues="openCues"
                    :totalPages="1"
                    :globalCues="cues"
                    :selectedScale="selectedScale"
                    :cuesListWidth="cuesListWidth"
                    :svgContentWidth="svgContentWidth"
                    :landscape="landscape"
                    @openCue="openCue"
                    @closeCue="closeCue"
                    :style="cuesListStyles()"
                />
            </div>
        </md-layout>
        <div class="page-break" />
    </md-layout>
</template>

<script>
import { mapGetters } from 'vuex';

import ProjectPage from '@/components/print/print-page';
import ProjectCueList from '@/components/print/cues/cue-list';

const ProjectPages = {
    name: 'projectPrintPages',
    props: [
        'landscape',
        'page',
        'locked',
        'showMode'
    ],
    data: function() {
        return {
            svgOffsetStart: 0,
            svgOffsetEnd: 0,
            cueHeight: 22,
            cueOpenHeight: 180,
            cueBottomPadding: 5,
            svgHeight: 100,
            svgContentWidth: 100,
            pageReady: false,
            selectedScale: localStorage.getItem('page-print-scale') || 1,
            openCues: [],
            cuesListWidth: 500,
            marginPad: { left: 0, right: 0 }
        };
    },
    computed: {
        ...mapGetters([
            'project',
            'user',
            'layerByCue',
            'visibleCues',
            'cues',
            'currentCueLayer',
            'bookmarks',
            'floatingCues',
            'cuePlacement',
            'getDefaultCuePlacementPrint',
            'getPrintCuePlacement',
            'getCueLineOpacity',
            'getCueFontSize',
            'printCuesInMargin'
        ]),
        inMargin: function() {
            return this.printCuesInMargin;
        },
        innerMarginStyle: function() {
            if (!this.inMargin) return {};
            return {
                position: 'relative',
                paddingLeft: `${this.marginPad.left}px`,
                paddingRight: `${this.marginPad.right}px`
            };
        },
        positionedCues: function() {
            const allCues = this.visibleCues(this.page.pageNumber);
            allCues.sort((a, b) => {
                if (a.y === b.y) {
                    return a.x - b.x;
                } 

                return a.y - b.y;
            });
            const positionedCues = [];

            const floatingCuesIDs = Object.keys(this.floatingCues);

            for (const cueItem of allCues) {
                // Note: structuredClone() not available in Node 11.15.0, using JSON.parse(JSON.stringify()) as fallback
                const cue = JSON.parse(JSON.stringify(cueItem));
                const defaultCuePlacement = this.getPrintCuePlacement(cue.layer);
                const isFloatingCue = floatingCuesIDs.includes(cue.id);

                cue.currentHeight = this.getCueBodyHeight(cue);
                if (cue.y <= 1 && cue.x <= 1) {
                    cue.offsetY = cue.y * this.svgHeight;
                    cue.offsetX = (cue.x * this.svgContentWidth) + this.svgOffsetStart;
                } else {
                    cue.offsetY = cue.y;
                    cue.offsetX = cue.x + this.svgOffsetStart;
                }

                if (positionedCues.length > 0) {
                    let otherCue = null;
                    for (let p = positionedCues.length - 1; p >= 0; p--) {
                        if (!floatingCuesIDs.includes(positionedCues[p].id) || this.landscape) {
                            otherCue = positionedCues[p];
                            break;
                        }
                    }
                    if (otherCue) {
                        if ((cue.offsetY - this.getCueHeaderHeight(cue)) + this.getCuePointHeight(cue) < (otherCue.offsetY + otherCue.currentHeight) + this.getCuePointHeight(otherCue)) {
                            if (defaultCuePlacement === 'left' || defaultCuePlacement === 'leftEdge') {
                                cue.offsetY = (otherCue.offsetY + otherCue.currentHeight + this.getCueHeaderHeight(cue) + this.getCuePointHeight(otherCue)) - (this.getCuePointHeight(cue) + (2.2 * this.selectedScale));
                            }
                            if (defaultCuePlacement === 'right' || defaultCuePlacement === 'rightEdge') {
                                cue.offsetY = (otherCue.offsetY + otherCue.currentHeight + this.getCueHeaderHeight(cue) + this.getCuePointHeight(otherCue)) - (this.getCuePointHeight(cue) + (2.6 * this.selectedScale));
                            }
                        }
                    }
                }

                if (cue.cuePlacement === 'right') {
                    cue.lineX1 = cue.offsetX + Math.min((cue.width * this.selectedScale), cue.type === 'position' ? 6 : 9999);
                    cue.lineY1 = this.cuePosTop(cue) + ((cue.type === 'position' ? (cue.height / 2) : cue.height) * this.selectedScale);
                    cue.lineX2 = this.svgOffsetEnd;
                    cue.lineY2 = cue.offsetY + ((cue.type === 'position' ? (cue.height / 2) : cue.height) * this.selectedScale);
                } else if (cue.cuePlacement === 'left') {
                    cue.lineX1 = this.cuesListWidth - 15;
                    cue.lineY1 = cue.offsetY + ((cue.type === 'position' ? (cue.height / 2) : cue.height) * this.selectedScale);
                    cue.lineX2 = this.cuesListWidth + cue.offsetX;
                    cue.lineY2 = this.cuePosTop(cue) + ((cue.type === 'position' ? (cue.height / 2) : cue.height) * this.selectedScale);
                } else if (defaultCuePlacement === 'right') {
                    const cueFixPos = window.devicePixelRatio === 1.25 ? 3 : 0;
                    cue.lineX1 = cue.offsetX + Math.min((cue.width * this.selectedScale), cue.type === 'position' ? cueFixPos : 9999);
                    cue.lineY1 = this.cuePosTop(cue) + ((cue.type === 'position' ? (cue.height / 2) : cue.height) * this.selectedScale);
                    cue.lineX2 = this.svgOffsetEnd - 15 - 2;
                    cue.lineY2 = cue.offsetY + ((cue.type === 'position' ? (cue.height / 2) : cue.height) * this.selectedScale);
                } else if (defaultCuePlacement === 'left') {
                    const cueFixPos = window.devicePixelRatio === 1.25 ? 3 : 1;
                    cue.lineX1 = 2;
                    cue.lineY1 = cue.offsetY + ((cue.type === 'position' ? (cue.height / 2) : cue.height) * this.selectedScale);
                    cue.lineX2 = cue.offsetX + cueFixPos;
                    cue.lineY2 = this.cuePosTop(cue) + ((cue.type === 'position' ? (cue.height / 2) : cue.height) * this.selectedScale);
                } else if (defaultCuePlacement === 'leftEdge') {
                    // Cue card sits out in the LEFT margin (mirrors editor Margin View).
                    const cueFixPos = window.devicePixelRatio === 1.25 ? 3 : 1;
                    cue.lineX1 = 2 - 17;
                    cue.lineY1 = cue.offsetY + (((cue.type === 'position' ? (cue.height / 2) : cue.height) * this.selectedScale) - 0.5);
                    cue.lineX2 = (cue.offsetX + cueFixPos) + 2;
                    cue.lineY2 = this.cuePosTop(cue) + ((cue.type === 'position' ? (cue.height / 2) : cue.height) * this.selectedScale);
                } else if (defaultCuePlacement === 'rightEdge') {
                    // Cue card sits out in the RIGHT margin.
                    cue.lineX1 = cue.offsetX + (Math.min((cue.width * this.selectedScale), cue.type === 'position' ? 6 : 9999) - 5);
                    cue.lineY1 = this.cuePosTop(cue) + ((cue.type === 'position' ? (cue.height / 2) : cue.height) * this.selectedScale);
                    cue.lineX2 = this.svgOffsetEnd;
                    cue.lineY2 = cue.offsetY + ((cue.type === 'position' ? (cue.height / 2) : cue.height) * this.selectedScale);
                }

                if (isFloatingCue) {
                    const isLeft = defaultCuePlacement === 'left' || defaultCuePlacement === 'leftEdge';
                    const isRight = defaultCuePlacement === 'right' || defaultCuePlacement === 'rightEdge';
                    if (isLeft && cue.type === 'selection') {
                        cue.lineX1 = cue.offsetX + (Math.min((cue.width * this.selectedScale), 5) - 4);
                    } else if (isLeft && cue.type === 'position') {
                        cue.lineX1 = cue.offsetX + (Math.min((cue.width * this.selectedScale), cue.type === 'position' ? 6 : 9999) - 4);
                    } else if (isRight && cue.type === 'position') {
                        cue.lineX1 = cue.offsetX + (Math.min((cue.width * this.selectedScale), cue.type === 'position' ? 6 : 9999) - 4);
                    } else if (isRight && cue.type === 'selection') {
                        cue.lineX1 = cue.offsetX + (cue.width * this.selectedScale);
                    }
                    cue.lineY1 = this.cuePosTop(cue) + ((cue.type === 'position' ? (cue.height / 2) : cue.height) * this.selectedScale);
                    cue.lineX2 = (this.floatingCues[cue.id].left) * this.selectedScale;
                    cue.lineY2 = (this.floatingCues[cue.id].top + ((12 * this.selectedScale) / this.selectedScale)) * this.selectedScale;
                }
                positionedCues.push(cue);
            }

            return positionedCues;
        },
        svgTrueHeight: function() {
            const lastElement = this.positionedCues[this.positionedCues.length - 1];
            if (lastElement) {
                return Math.max(this.svgHeight, lastElement.offsetY);
            }

            return this.svgHeight;
        }
    },
    methods: {
        cueColor(cue) {
            const layer = this.layerByCue(cue);
            if (layer) return layer.color;

            return 'black';
        },
        getCueBodyHeight(cue) {
            const cueIndex = this.openCues.findIndex(c => c.id === cue.id);
            const tempHeight = cueIndex >= 0 ? this.openCues[cueIndex].height - (((this.getCueFontSize(cue) || this.layerByCue(cue).titleFontSize || 14) * 1.1) + 1) : 0;
            return (tempHeight * this.selectedScale) + (this.cueBottomPadding * this.selectedScale);
        },
        getCueHeaderHeight(cue) {
            return (this.getCueFontSize(cue) || this.layerByCue(cue).titleFontSize || 14) * 1.1 * this.selectedScale;
        },
        getCuePointHeight(cue) {
            return (cue.type === 'position' ? (cue.height / 2) : cue.height) * this.selectedScale;
        },
        cuePosTop(cue) {
            if (cue.x <= 1 && cue.y <= 1) {
                return (cue.y * this.svgHeight) + 1;
            }
            return cue.y + 1;
        },
        pdfPaneOrderByCuePlacement() {
            return this.cuePlacement === 'right' ? 2 : 4;
        },
        lineOpacity(cue) {
            const lineOpacity = this.getCueLineOpacity(cue);
            if (cue.lineOpacity !== undefined) {
                return cue.lineOpacity / 100;
            }
            if (lineOpacity !== undefined) {
                return lineOpacity / 100;
            }
            return 0.3;
        },
        cueLineOpacityStyle(cue) {
            return '{opacity: ' + this.lineOpacity(cue) + ';}';
        },
        openCue(cue) {
            this.openCues.push({id: cue.id, height: cue.height / this.selectedScale});
        },
        closeCue(cue) {
            const cueIndex = this.openCues.findIndex(c => c.id === cue.id);
            this.openCues.splice(cueIndex, 1);
        },
        pageLoaded(data) {
            this.svgHeight = data.pageHeight;
            this.svgOffsetStart = data.offsetX;
            this.svgContentWidth = data.pageWidth;
            this.svgOffsetEnd = Math.min(data.width, data.pageWidth) + 15;

            const elmParent = document.querySelector('.ql-pages-container');
            if (elmParent) {
                this.cuesListWidth = 500;
            }

            if (data.viewport) {
                this.scale = this.selectedScale;
            }

            this.$store.commit('updatePageSize', {
                width: this.svgContentWidth,
                height: this.svgHeight
            });

            this.pageReady = true;

            const nAgt = navigator.userAgent;
            let browserName = navigator.appName;
            if (nAgt.includes('OPR')) {
                browserName = 'Opera';
            } else if (nAgt.includes('Edg')) {
                browserName = 'Microsoft Edge';
            } else if (nAgt.includes('MSIE')) {
                browserName = 'Microsoft Internet Explorer';
            } else if (nAgt.includes('Chrome')) {
                browserName = 'Chrome';
            } else if (nAgt.includes('Safari')) {
                browserName = 'Safari';
            } else if (nAgt.includes('Firefox')) {
                browserName = 'Firefox';
            } else if ((nAgt.lastIndexOf(' ') + 1) < nAgt.lastIndexOf('/')) {
                browserName = nAgt.substring(nAgt.lastIndexOf(' ') + 1, nAgt.lastIndexOf('/'));
            }

            let pageLoadTimeOut = 2000;
            if (browserName === 'Safari') {
                pageLoadTimeOut = 3000;
            }
            this.$nextTick(() => {
                setTimeout(() => {
                    if (this.inMargin) {
                        this.measureMarginPadding();
                    }
                    this.$emit('pageReadyFun', this.pageReady);
                }, pageLoadTimeOut);
            });
        },
        measureMarginPadding() {
            // In "cues in margin" mode the cue cards translate off the page into
            // the margins (absolute + transform), so they overflow .ql-pages-inner.
            // Measure how far the widest card on each side extends past the PDF page
            // and reserve that as padding, so the print sheet (@page, sized from
            // .ql-pages-inner by Utils.buildPrintPageStyle) includes the margins and
            // the cards aren't clipped. Mirrors the editor's dynamic margin reserve.
            const page = this.$el && this.$el.querySelector('.ql-page-cover');
            if (!page) return;
            const pageRect = page.getBoundingClientRect();
            const cards = this.$el.querySelectorAll('.ql-cue');
            let left = 0;
            let right = 0;
            cards.forEach((card) => {
                const r = card.getBoundingClientRect();
                if (!r.width) return;
                left = Math.max(left, pageRect.left - r.left);
                right = Math.max(right, r.right - pageRect.right);
            });
            const gap = 12;
            this.marginPad = {
                left: left > 0 ? Math.ceil(left + gap) : 0,
                right: right > 0 ? Math.ceil(right + gap) : 0
            };
        },
        cuesListStyles() {
            const styles = {
                order: 3,
                height: `${this.svgTrueHeight}px`
            };
            if (this.cuePlacement === 'right') {
                styles.right = `calc(100% - ${this.svgContentWidth}px)`;
                styles.maxWidth = `${this.svgContentWidth}px`;
            }
            return styles;
        }
    },
    components: {
        'project-page': ProjectPage,
        'project-cue-list': ProjectCueList
    },
    mounted: function() {}
};

export default ProjectPages;
</script>

<style lang="scss">
:root{
    --page-min-width: 100px;
    --cue-marker-font-size: 21px;
}
.app-print-mode {
    .ql-pages-container {
        width: 100%;
    }
    .ql-page-container {
        padding: .5em 0 0 0 ;
    }
    .vdr {
        position: absolute;
        box-sizing: border-box;
    }
    .ql-page-layout {
        position: relative;
        flex-wrap: wrap;
        margin-inline: auto;
        flex: unset;
    }
    .ql-page-graphics {
        position: absolute;
        z-index: 10;
        width: 100%;
        pointer-events: none;
        .ql-page-cue-line {
            stroke: #818181;
        }
    }

    .ql-page-graphics .resizeable {
        pointer-events: stroke;
        cursor: nwse-resize;
    }

    .ql-page-graphics .moveable {
        pointer-events: fill;
        cursor: move;
    }

    @media all {
        .page-break { display: none; }
    }

    @media print {
        /* @page size is injected at print time from the rendered content by
           Utils.buildPrintPageStyle (src/views/print.vue print()). Do NOT
           hardcode a sheet size here — that is the non-standard-page-size bug. */
        .page-break { display: block; page-break-before: always; }
        /* Keep each page composite whole on its own sheet. Without this, Safari's
           print path (unlike its PDF export) splits a page across two sheets when
           the content is a hair taller than the printable area. */
        .ql-page-layout, .ql-pages-inner {
            page-break-inside: avoid;
            break-inside: avoid;
        }
        .ql-cue.md-card {
            box-shadow: none;
        }
    }
}
</style>


// WEBPACK FOOTER //
// print-pages.vue?f4f3c69a